import React, { useEffect, useState } from "react";
import axios from "axios";
import { Authorization } from "../../Context/Authorization";
import Preloader from "../../shared/PreLoaders";
import "../../dashboard/reporttable.css";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

const inputStyle = { width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff", };

const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "flex", alignItems: "center", gap: 5, };
const TogglePill = ({
    label,
    icon,
    checked,
    onChange,
    disabled = false,
}) => (
    <label
        style={{
            display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 999, border: "1px solid #2E7D32", cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, background: checked ? "#E8F5E9" : "#fff",
        }}
    >
        <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={onChange}
            style={{ display: "none" }}
        />
        <i className={`mdi ${icon}`} />
        {label}
    </label>
);

const AuditRenewalReview = () => {
    const Api = process.env.REACT_APP_API;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    /* Master Data */
    const [circles, setCircles] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [jfmcTypes, setJfmcTypes] = useState([]);
    /* Report */
    const [reviewData, setReviewData] = useState([]);
    const [auditData, setAuditData] = useState([]);
    const [renewalData, setRenewalData] = useState([]);
    /* Filters */
    const today = new Date();
    const [selectedMonth, setSelectedMonth] = useState(
        `${today.getFullYear()}-${String(today.getMonth()).padStart(2, "0")}`
    );
    const [year, month] = selectedMonth.split("-");
    const [selectedCircle, setSelectedCircle] = useState("");
    const [selectedDivision, setSelectedDivision] = useState("");
    const [selectedRange, setSelectedRange] = useState("");
    const [selectedJfmcType, setSelectedJfmcType] = useState("");
    const [selectedType, setSelectedType] = useState("audit");
    const [showCircle, setShowCircle] = useState(false);
    const [showDivision, setShowDivision] = useState(false);
    const [expandedDivisions, setExpandedDivisions] = useState({});
    const [expandedRanges, setExpandedRanges] = useState({});

    /* -------------------------------------- */
    const fetchMasterData = async () => {
        try {
            const config = Authorization();

            const [circleRes, divisionRes, jfmcRes] = await Promise.all([
                axios.get(`${Api}/api/circles`, config),
                axios.get(`${Api}/api/divisions`, config),
                axios.get(`${Api}/api/jfmc-types`, config),
            ]);

            setCircles(circleRes.data || []);
            setDivisions(divisionRes.data || []);

            const jfmcTypeList = jfmcRes.data || [];
            setJfmcTypes(jfmcTypeList);

            //Automatically select VFC JFMC Type
            const defaultJfmcType = jfmcTypeList.find(
                (item) => item.jfmcTypeName === "VFC"
            );

            if (defaultJfmcType) {
                setSelectedJfmcType(defaultJfmcType._id);
            }
        } catch (err) {
            console.error(err);
            setError("Failed to load filters.");
        }
    };
    /* -------------------------------------- */
    const fetchEcoTourismReview = async (
        jfmcTypeId = selectedJfmcType
    ) => {
        try {
            setLoading(true);

            const config = Authorization();

            const payload = {
                month,
                year,
                circleId: selectedCircle || undefined,
                divisionId: selectedDivision || undefined,
                rangeId: selectedRange || undefined,
                jfmcTypeId,
                type: selectedType,
            };

            const { data } = await axios.post(
                `${Api}/api/renewal/audit/review`,
                payload,
                config
            );

            setReviewData(data.data || []);
        } catch (error) {
            console.error(error);
            setReviewData([]);

            toast.error(
                error.response?.data?.message ||
                "Failed to load review data."
            );
        } finally {
            setLoading(false);
        }
    };
    /* -------------------------------------- */
    const formatLastUpdated = (date) => {
        if (!date) return "-";

        return new Date(date).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };
    useEffect(() => {
        fetchMasterData();
    }, []);
    /* -------------------------------------- */
    useEffect(() => {
        fetchEcoTourismReview();
    }, [selectedMonth, selectedCircle, selectedDivision, selectedJfmcType, selectedType]);
    const activeJfmc =
        jfmcTypes.find(item => item._id === selectedJfmcType) || {
            jfmcTypeName: "All JFMC Types",
        };
    /* -------------------------------------- */
    const badge = (value, type) => {
        const styles = {
            total: {
                bg: "#EEF2FF",
                color: "#3730A3",
            },
            held: {
                bg: "#DCFCE7",
                color: "#166534",
            },
            pending: {
                bg: "#FEE2E2",
                color: "#991B1B",
            },
        };
        const style = styles[type];
        return (
            <span
                style={{ display: "inline-block", minWidth: 40, textAlign: "center", padding: "4px 10px", borderRadius: 20, background: style.bg, color: style.color, fontWeight: 600, fontSize: 12, }}>
                {value}
            </span>
        );
    };
    /* ---------------- Summary ---------------- */
    const totals = reviewData.reduce((acc, item) => {

        acc.totalVfcs += item.totalVfcs || 0;
        acc.completed += item.completed || 0;
        acc.pending += item.pending || 0;

        acc.totalCircles = reviewData.length;

        if (item.divisions) {
            acc.totalDivisions += Object.keys(item.divisions).length;
        }

        if (item.ranges) {
            acc.totalRanges += Object.keys(item.ranges).length;
        }

        return acc;

    }, {
        totalVfcs: 0,
        completed: 0,
        pending: 0,
        totalCircles: 0,
        totalDivisions: 0,
        totalRanges: 0
    });

    const toggleDivision = (id) => {
        setExpandedDivisions(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };
    const toggleRange = (id) => {
        setExpandedRanges(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };
    /* -------------------------------------- */
    const exportAuditRenewalReport = () => {
        // =====================================================
        // CURRENT TYPE / DATA
        // =====================================================

        const currentType =
            (selectedType || "audit").toLowerCase();

        const currentData =
            currentType === "renewal"
                ? renewalData
                : auditData;

        if (!currentData || currentData.length === 0) {
            alert(
                `No ${currentType === "audit"
                    ? "Audit"
                    : "Renewal"
                } data available to export`
            );
            return;
        }

        // =====================================================
        // PERCENTAGE
        // =====================================================

        const calculatePercentage = (completed, total) => {
            if (!total || total === 0) {
                return "0.00%";
            }

            return `${(
                (completed / total) *
                100
            ).toFixed(2)}%`;
        };

        // =====================================================
        // JFMC TYPE
        // =====================================================

        const selectedJFMCType = jfmcTypes.find(
            (type) =>
                String(type._id) ===
                String(selectedJfmcType)
        );

        const jfmcTypeName =
            selectedJFMCType?.jfmcTypeName || "JFMC";

        const totalJfmcHeading =
            `Total JFMC (${jfmcTypeName})`;

        const completedJfmcHeading =
            `Completed JFMC (${jfmcTypeName})`;

        const pendingJfmcHeading =
            `Pending JFMC (${jfmcTypeName})`;
        let reportYear;
        let reportMonth;
        if (
            typeof month === "string" &&
            /^\d{4}-\d{2}$/.test(month)
        ) {
            const [y, m] = month.split("-");
            reportYear = Number(y);
            reportMonth = Number(m);
        } else {
            reportYear = Number(year);
            reportMonth = Number(month);
        }
        const monthDate = new Date(
            reportYear,
            reportMonth - 1,
            1
        );
        const monthName = monthDate.toLocaleString(
            "en-US",
            {
                month: "long"
            }
        );
        const reportType =
            currentType === "audit"
                ? "Audit"
                : "Renewal";
        const reportTitle =
            `${reportType} Summary for the Month of ${monthName} ${reportYear}`;
        let nameColumn = "Circle";
        if (selectedRange) {
            nameColumn = "VFC";
        } else if (selectedDivision) {
            nameColumn = "Range";
        } else if (selectedCircle) {
            nameColumn = "Division";
        }
        let excelData = [];

        if (selectedRange) {
            // VFC wise
            excelData = currentData.map((vfc) => {
                const total = 1;
                const completed = vfc.completed ? 1 : 0;
                const pending = total - completed;

                return {
                    VFC: vfc.name || vfc.vfcName || "-",
                    [totalJfmcHeading]: total,
                    [completedJfmcHeading]: completed,
                    [pendingJfmcHeading]: pending,
                    [`${reportType} Completion %`]:
                        calculatePercentage(completed, total),
                };
            });
        }
        else if (selectedDivision) {
            // Range wise
            currentData.forEach((range) => {
                const total = Number(range.totalVfcs || 0);
                const completed = Number(range.completed || 0);
                const pending = Number(
                    range.pending ?? (total - completed)
                );

                excelData.push({
                    Range: range.name || range.rangeName || "-",
                    [totalJfmcHeading]: total,
                    [completedJfmcHeading]: completed,
                    [pendingJfmcHeading]: pending,
                    [`${reportType} Completion %`]:
                        calculatePercentage(completed, total),
                });
            });
        }
        else if (selectedCircle) {
            // Division wise
            currentData.forEach((division) => {
                const total = Number(division.totalVfcs || 0);
                const completed = Number(division.completed || 0);
                const pending = Number(
                    division.pending ?? (total - completed)
                );

                excelData.push({
                    Division: division.name || division.divisionName || "-",
                    [totalJfmcHeading]: total,
                    [completedJfmcHeading]: completed,
                    [pendingJfmcHeading]: pending,
                    [`${reportType} Completion %`]:
                        calculatePercentage(completed, total),
                });
            });
        }
        else {
            // No Circle selected
            // API returns circles, export Division-wise
            currentData.forEach((circle) => {
                Object.values(circle.divisions || {}).forEach((division) => {
                    const total = Number(division.totalVfcs || 0);
                    const completed = Number(division.completed || 0);
                    const pending = Number(
                        division.pending ?? (total - completed)
                    );

                    excelData.push({
                        Division: division.name || "-",
                        [totalJfmcHeading]: total,
                        [completedJfmcHeading]: completed,
                        [pendingJfmcHeading]: pending,
                        [`${reportType} Completion %`]:
                            calculatePercentage(completed, total),
                    });
                });
            });
        }
        if (excelData.length === 0) {
            alert(
                `No ${reportType} data available to export`
            );
            return;
        }
        const headers =
            Object.keys(excelData[0]);
        const rows =
            excelData.map((row) =>
                headers.map(
                    (header) => row[header]
                )
            );
        const worksheet =
            XLSX.utils.aoa_to_sheet([
                [reportTitle],
                [],
                headers,
                ...rows
            ]);
        worksheet["!merges"] = [
            {
                s: {
                    r: 0,
                    c: 0
                },
                e: {
                    r: 0,
                    c: headers.length - 1
                }
            }
        ];
        worksheet["!cols"] =
            headers.map((header) => ({
                wch: Math.max(
                    header.length + 3,
                    18
                )
            }));
        const workbook =
            XLSX.utils.book_new();
        const sheetName =
            currentType === "audit"
                ? "Audit Progress"
                : "Renewal Progress";

        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            sheetName
        );
        const fileName =
            currentType === "audit"
                ? `Audit_Progress_${monthName}_${reportYear}.xlsx`
                : `Renewal_Progress_${monthName}_${reportYear}.xlsx`
        XLSX.writeFile(
            workbook,
            fileName
        );
    };
    if (loading) return <Preloader loading />;
    if (error) return <p className="text-danger">{error}</p>;
    return <div><div className="rd-page-header">
        <div className="rd-header-left">
            <span className="page-title-icon">
                <i className="mdi mdi-clipboard-check-multiple-outline" />
            </span>
            <div>
                <h3 className="rd-page-title">
                    Audit & Renewal Review Dashboard
                </h3>
                <p className="rd-page-sub">
                    Month-wise Audit & Renewal Review
                </p>
            </div>
        </div>
    </div>
        {/* ---------------- Filters ---------------- */}
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "20px 24px", marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                <span style={{ width: 30, height: 30, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
                    <i className="mdi mdi-filter-variant" />
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Filter Report</span>
                <span style={{ fontSize: 11, color: "#A0AEC0", marginLeft: 4 }}>Toggle filters and select a date to view records</span>
            </div>
            {/* Toggle pills */}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                <TogglePill label="Circle" icon="mdi-circle-outline" checked={showCircle} disabled={showDivision} onChange={(e) => { setShowCircle(e.target.checked); if (!e.target.checked) setSelectedCircle(""); }} />
                <TogglePill label="Division" icon="mdi-domain" checked={showDivision} disabled={showCircle} onChange={(e) => { setShowDivision(e.target.checked); if (!e.target.checked) setSelectedDivision(""); }} />
            </div>
            {/* Conditional selects */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 12, marginBottom: 16 }}>
                <div >
                    <label style={labelStyle}>
                        <i
                            className="mdi mdi-calendar-month"
                            style={{ color: "#43A047" }}
                        />
                        Month
                    </label>
                    <input
                        type="month"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        style={inputStyle}
                    />
                </div>
                <div>
                    <label style={labelStyle}>
                        <i
                            className="mdi mdi-shape-outline"
                            style={{ color: "#43A047" }}
                        />
                        JFMC Type
                    </label>
                    <select
                        value={selectedJfmcType}
                        onChange={(e) => setSelectedJfmcType(e.target.value)}
                        style={inputStyle}
                    >
                        <option value="">All JFMC Types</option>

                        {jfmcTypes
                            .sort((a, b) => a.jfmcTypeName.localeCompare(b.jfmcTypeName))
                            .map((j) => (
                                <option key={j._id} value={j._id}>
                                    {j.jfmcTypeName}
                                </option>
                            ))}
                    </select>
                </div>
                <div>
                    <label style={labelStyle}>
                        <i
                            className="mdi mdi-shape-outline"
                            style={{ color: "#43A047" }}
                        />
                        Select Type
                    </label>
                    <select
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.target.value)}
                        style={inputStyle}
                    >
                        <option value="audit">Audit</option>
                        <option value="renewal">Renewal</option>

                    </select>
                </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 12, marginBottom: 16 }}>{showCircle && (
                <div>
                    <label style={labelStyle}><i className="mdi mdi-circle-outline" style={{ color: "#43A047" }} /> Circle</label>
                    <select value={selectedCircle} onChange={(e) => setSelectedCircle(e.target.value)} style={inputStyle} onFocus={(e) => e.target.style.borderColor = "#4CAF50"} onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}>
                        <option value="">— Select —</option>
                        {circles.sort((a, b) => a.circleName.localeCompare(b.circleName)).map((c) => <option key={c._id} value={c._id}>{c.circleName}</option>)}
                    </select>
                </div>
            )}
                {showDivision && (
                    <div>
                        <label style={labelStyle}><i className="mdi mdi-domain" style={{ color: "#43A047" }} /> Division</label>
                        <select value={selectedDivision} onChange={(e) => setSelectedDivision(e.target.value)} style={inputStyle} onFocus={(e) => e.target.style.borderColor = "#4CAF50"} onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}>
                            <option value="">— Select —</option>
                            {divisions.sort((a, b) => a.divisionName.localeCompare(b.divisionName)).map((d) => <option key={d._id} value={d._id}>{d.divisionName}</option>)}
                        </select>
                    </div>
                )}
            </div>
        </div>
        <div className="dashboard-sections">
            <div className="jfmc-section">
                <h3 className="jfmc-title">
                    {activeJfmc.jfmcTypeName} — Compliance Overview
                </h3>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
                        gap: 16,
                    }}
                >
                    {/* Level Count */}
                    <div className="stat-card card-blue stat-centered">
                        <div className="stat-icon-big icon-blue">
                            <i
                                className={
                                    selectedRange
                                        ? "fas fa-building"
                                        : selectedDivision
                                            ? "fas fa-map"
                                            : selectedCircle
                                                ? "fas fa-layer-group"
                                                : "fas fa-sitemap"
                                }
                            />
                        </div>
                        <div className="stat-title">
                            {selectedRange
                                ? "VFCs"
                                : selectedDivision
                                    ? "Ranges"
                                    : selectedCircle
                                        ? "Divisions"
                                        : "Circles"}
                        </div>
                        <div
                            style={{
                                fontSize: 34,
                                fontWeight: 700,
                                marginTop: 12,
                            }}
                        >
                            {selectedRange
                                ? totals.totalVfcs
                                : selectedDivision
                                    ? totals.totalRanges
                                    : selectedCircle
                                        ? totals.totalDivisions
                                        : totals.totalCircles}
                        </div>
                    </div>
                    {/* Total JFMC */}
                    <div className="stat-card card-purple stat-centered">
                        <div className="stat-icon-big icon-purple">
                            <i className="fas fa-users-cog" />
                        </div>
                        <div className="stat-title">
                            Total JFMC
                        </div>
                        <div
                            style={{
                                fontSize: 34,
                                fontWeight: 700,
                                marginTop: 12,
                            }}
                        >
                            {totals.totalVfcs}
                        </div>
                    </div>
                    {/* Completed */}
                    <div className="stat-card card-green stat-centered">
                        <div className="stat-icon-big icon-green">
                            <i className="fas fa-check-circle" />
                        </div>

                        <div className="stat-title">
                            Completed
                        </div>

                        <div
                            style={{
                                fontSize: 34,
                                fontWeight: 700,
                                marginTop: 12,
                            }}
                        >
                            {totals.completed}
                        </div>
                    </div>

                    {/* Pending */}
                    <div className="stat-card card-orange stat-centered">
                        <div className="stat-icon-big icon-orange">
                            <i className="fas fa-times-circle" />
                        </div>

                        <div className="stat-title">
                            Pending
                        </div>

                        <div
                            style={{
                                fontSize: 34,
                                fontWeight: 700,
                                marginTop: 12,
                            }}
                        >
                            {totals.pending}
                        </div>
                    </div>

                </div>
            </div>
        </div>
        {/* ---------------- Summary Table ---------------- */}
        <div
            style={{
                marginTop: 24,
                background: "#fff",
                borderRadius: 12,
                border: "1px solid #E2E8F0",
                boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                overflow: "hidden",
            }}
        >
            {/* Card Header */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "14px 20px",
                    borderBottom: "1px solid #EDF2F7",
                }}
            >
                <span
                    style={{
                        width: 36,
                        height: 36,
                        background: "linear-gradient(135deg,#1B5E20,#43A047)",
                        borderRadius: 9,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: 16,
                    }}
                >
                    <i
                        className={`mdi ${selectedType === "renewal"
                            ? "mdi-file-refresh-outline"
                            : "mdi-clipboard-check-outline"
                            }`}
                    />
                </span>

                <div>
                    <div
                        style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: "#1A202C",
                        }}
                    >
                        {selectedType === "renewal"
                            ? "Renewal Review Summary"
                            : selectedType === "audit"
                                ? "Audit Review Summary"
                                : "Audit & Renewal Review Summary"}
                    </div>

                    <div
                        style={{
                            fontSize: 11,
                            color: "#A0AEC0",
                        }}
                    >
                        {selectedRange
                            ? "VFC Wise"
                            : selectedDivision
                                ? "Range Wise"
                                : selectedCircle
                                    ? "Division Wise"
                                    : "Circle Wise"}
                    </div>
                </div>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        marginLeft: "auto",
                    }}
                    className="jfmc-switch"
                >
                    <button
                        onClick={exportAuditRenewalReport}
                        className="btn btn-success"
                    >
                        Export Excel
                    </button>
                </div>
            </div>
            {/* Table */}
            <div style={{ overflowX: "auto" }}>
                <table
                    style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        minWidth: "1200px",
                    }}
                >
                    <thead>
                        <tr
                            style={{
                                background: "#2E7D32",
                                color: "#fff",
                            }}
                        >
                            <th style={{ padding: 12, textAlign: "left" }}>
                                {selectedRange
                                    ? "VFC"
                                    : selectedDivision
                                        ? "Range"
                                        : selectedCircle
                                            ? "Division"
                                            : "Circle"}
                            </th>
                            {selectedRange ? (
                                <>
                                    <th style={{ padding: 12, textAlign: "center" }}>
                                        Status
                                    </th>

                                    <th style={{ padding: 12, textAlign: "center" }}>
                                        Meeting Date
                                    </th>
                                </>
                            ) : (
                                <>
                                    <th style={{ padding: 12, textAlign: "center" }}>
                                        Total JFMC
                                    </th>

                                    <th style={{ padding: 12, textAlign: "center" }}>
                                        Held
                                    </th>

                                    <th style={{ padding: 12, textAlign: "center" }}>
                                        Not Held
                                    </th>

                                    <th style={{ padding: 12 }}>
                                        {selectedCircle
                                            ? "Range Progress"
                                            : selectedDivision
                                                ? "VFC Progress"
                                                : "Division Progress"}
                                    </th>
                                </>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {reviewData.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={selectedRange ? 3 : 6}
                                    style={{
                                        textAlign: "center",
                                        padding: 30,
                                        color: "#999",
                                    }}
                                >
                                    No Records Found
                                </td>
                            </tr>
                        ) : (
                            reviewData.map((item, index) => (
                                <tr
                                    key={item._id}
                                    style={{
                                        background:
                                            index % 2 === 0 ? "#fff" : "#F8FAFD",
                                    }}
                                >
                                    {/* Name */}
                                    <td
                                        style={{
                                            padding: "12px 15px",
                                            fontWeight: 600,
                                            color: "#1565C0",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "6px",
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <i className="mdi mdi-map-marker-outline" />

                                            <span>{item.name}</span>

                                            {item.lastUpdatedDate && (
                                                <span
                                                    style={{
                                                        fontSize: "12px",
                                                        fontWeight: 700,
                                                        color: "#1565C0",
                                                    }}
                                                >
                                                    - Last Updated:{" "}
                                                    {new Date(item.lastUpdatedDate).toLocaleDateString("en-GB")}
                                                </span>
                                            )}
                                        </div>
                                    </td>

                                    {selectedRange ? (
                                        <>
                                            {/* Status */}
                                            <td
                                                style={{
                                                    textAlign: "center",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: "inline-block",
                                                        padding: "5px 12px",
                                                        borderRadius: 20,
                                                        fontSize: 12,
                                                        fontWeight: 600,
                                                        background: item.completed
                                                            ? "#DCFCE7"
                                                            : "#FEE2E2",
                                                        color: item.completed
                                                            ? "#166534"
                                                            : "#991B1B",
                                                    }}
                                                >
                                                    {item.completed ? "Held" : "Not Held"}
                                                </span>
                                            </td>

                                            {/* Meeting Date */}
                                            <td
                                                style={{
                                                    textAlign: "center",
                                                }}
                                            >
                                                {item.meetingDate ? (
                                                    new Date(
                                                        item.meetingDate
                                                    ).toLocaleDateString("en-GB")
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            {/* Total VFC */}
                                            <td
                                                style={{
                                                    textAlign: "center",
                                                }}
                                            >
                                                {badge(item.totalVfcs, "total")}
                                            </td>

                                            {/* Held */}
                                            <td
                                                style={{
                                                    textAlign: "center",
                                                }}
                                            >
                                                {badge(item.completed, "held")}
                                            </td>

                                            {/* Not Held */}
                                            <td
                                                style={{
                                                    textAlign: "center",
                                                }}
                                            >
                                                {badge(item.pending, "pending")}
                                            </td>

                                            <td style={{ padding: 12, minWidth: 520 }}>
                                                {!selectedCircle && !selectedDivision &&
                                                    Object.values(item.divisions || {}).map((division) => (
                                                        <div
                                                            key={division._id}
                                                            style={{
                                                                marginBottom: 15,
                                                                border: "1px solid #E5E7EB",
                                                                borderRadius: 10,
                                                                overflow: "hidden"
                                                            }}
                                                        >
                                                            {/* Division Header */}

                                                            <div
                                                                onClick={() => toggleDivision(division._id)}
                                                                style={{
                                                                    display: "flex",
                                                                    justifyContent: "space-between",
                                                                    alignItems: "center",
                                                                    padding: "12px 15px",
                                                                    cursor: "pointer",
                                                                    background:
                                                                        division.pending === 0
                                                                            ? "#ECFDF5"
                                                                            : "#FEF2F2"
                                                                }}
                                                            >
                                                                <div>
                                                                    <div
                                                                        style={{
                                                                            fontWeight: 700,
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            gap: "6px",
                                                                            flexWrap: "wrap",
                                                                            color:
                                                                                division.pending === 0
                                                                                    ? "#166534"
                                                                                    : "#991B1B"
                                                                        }}
                                                                    >
                                                                        <span>Division : {division.name}</span>
                                                                        {division.lastUpdatedDate && (
                                                                            <span
                                                                                style={{
                                                                                    fontSize: 12,
                                                                                    fontWeight: 500,
                                                                                    color: "#1565C0",
                                                                                }}
                                                                            >
                                                                                - Last Updated:{" "}
                                                                                {new Date(division.lastUpdatedDate).toLocaleDateString("en-GB")}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div
                                                                        style={{
                                                                            fontSize: 12,
                                                                            color: "#666"
                                                                        }}
                                                                    >
                                                                        {division.completed}/{division.totalVfcs} Completed
                                                                    </div>
                                                                </div>
                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: 15
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            padding: "4px 12px",
                                                                            borderRadius: 20,
                                                                            background:
                                                                                division.pending === 0
                                                                                    ? "#DCFCE7"
                                                                                    : "#FEE2E2",
                                                                            color:
                                                                                division.pending === 0
                                                                                    ? "#166534"
                                                                                    : "#991B1B",
                                                                            fontSize: 12,
                                                                            fontWeight: 600
                                                                        }}
                                                                    >
                                                                        {division.pending === 0
                                                                            ? "Completed"
                                                                            : "Pending"}
                                                                    </span>

                                                                    <i
                                                                        className={`mdi ${expandedDivisions[division._id]
                                                                            ? "mdi-chevron-up"
                                                                            : "mdi-chevron-down"
                                                                            }`}
                                                                        style={{ fontSize: 22 }}
                                                                    />
                                                                </div>
                                                            </div>

                                                            {/* Expanded Division */}

                                                            {expandedDivisions[division._id] && (
                                                                <div
                                                                    style={{
                                                                        marginLeft: 20,
                                                                        marginTop: 10
                                                                    }}
                                                                >
                                                                    {Object.values(division.ranges || {}).map((range) => (
                                                                        <div
                                                                            key={range._id}
                                                                            style={{
                                                                                marginBottom: 15,
                                                                                borderLeft: "3px solid #22C55E",
                                                                                paddingLeft: 15
                                                                            }}
                                                                        >
                                                                            {/* Range Header */}

                                                                            <div
                                                                                onClick={() => toggleRange(range._id)}
                                                                                style={{
                                                                                    display: "flex",
                                                                                    justifyContent: "space-between",
                                                                                    alignItems: "center",
                                                                                    cursor: "pointer",
                                                                                    padding: "10px 0"
                                                                                }}
                                                                            >
                                                                                <div>
                                                                                    <strong>
                                                                                        Range : {range.name}
                                                                                    </strong>

                                                                                    <div
                                                                                        style={{
                                                                                            fontSize: 12,
                                                                                            color: "#666"
                                                                                        }}
                                                                                    >
                                                                                        {range.completed}/
                                                                                        {range.totalVfcs} Completed
                                                                                    </div>
                                                                                    <div
                                                                                        style={{
                                                                                            fontSize: 12,
                                                                                            color: "#888"
                                                                                        }}
                                                                                    >
                                                                                        Last Updated :
                                                                                        {new Date(range.lastUpdatedDate).toLocaleDateString("en-GB")}
                                                                                    </div>
                                                                                </div>

                                                                                <i
                                                                                    className={`mdi ${expandedRanges[range._id]
                                                                                        ? "mdi-chevron-up"
                                                                                        : "mdi-chevron-down"
                                                                                        }`}
                                                                                    style={{ fontSize: 22 }}
                                                                                />
                                                                            </div>

                                                                            {/* Expanded Range */}

                                                                            {expandedRanges[range._id] &&
                                                                                range.vfcs.map((vfc) => (
                                                                                    <div
                                                                                        key={vfc._id}
                                                                                        style={{
                                                                                            marginTop: 10,
                                                                                            marginBottom: 10,
                                                                                            marginLeft: 20,
                                                                                            border: "1px solid #E5E7EB",
                                                                                            borderRadius: 8,
                                                                                            background: "#fff",
                                                                                            padding: 12
                                                                                        }}
                                                                                    >
                                                                                        <div
                                                                                            style={{
                                                                                                display: "flex",
                                                                                                justifyContent: "space-between",
                                                                                                alignItems: "center",
                                                                                                marginBottom: 10
                                                                                            }}
                                                                                        >
                                                                                            <strong>{vfc.name}</strong>

                                                                                            <span
                                                                                                style={{
                                                                                                    padding: "4px 10px",
                                                                                                    borderRadius: 20,
                                                                                                    background: vfc.completed ? "#DCFCE7" : "#FEE2E2",
                                                                                                    color: vfc.completed ? "#166534" : "#991B1B",
                                                                                                    fontSize: 11,
                                                                                                    fontWeight: 600
                                                                                                }}
                                                                                            >
                                                                                                {vfc.completed ? "Completed" : "Pending"}
                                                                                            </span>
                                                                                            <div
                                                                                                style={{
                                                                                                    fontSize: 12,
                                                                                                    color: "#666",
                                                                                                    marginTop: 5
                                                                                                }}
                                                                                            >
                                                                                                Last Updated :
                                                                                                {vfc.lastUpdatedDate ? new Date(vfc.lastUpdatedDate).toLocaleDateString("en-GB") : "-"}
                                                                                            </div>
                                                                                        </div>

                                                                                        {vfc.completed &&
                                                                                            (selectedType || "audit").toLowerCase() === "audit" && (
                                                                                                <>
                                                                                                    <div><b>Audit Date:</b> {vfc.auditDate ? new Date(vfc.auditDate).toLocaleDateString("en-GB") : "-"}</div>

                                                                                                    <div><b>Auditor:</b> {vfc.auditorName || "-"}</div>

                                                                                                    <div><b>Balance:</b> ₹ {Number(vfc.balanceDuringAudit || 0).toLocaleString("en-IN")}</div>

                                                                                                    {vfc.uploadDocument && (
                                                                                                        <div>
                                                                                                            <a
                                                                                                                href={vfc.uploadDocument}
                                                                                                                target="_blank"
                                                                                                                rel="noreferrer"
                                                                                                            >
                                                                                                                View Audit Document
                                                                                                            </a>
                                                                                                        </div>
                                                                                                    )}
                                                                                                </>
                                                                                            )}

                                                                                        {vfc.completed &&
                                                                                            (selectedType || "audit").toLowerCase() === "renewal" && (
                                                                                                <>
                                                                                                    <div><b>Renewal No:</b> {vfc.renewalNo || "-"}</div>

                                                                                                    <div><b>Renewal Date:</b> {vfc.renewalDate ? new Date(vfc.renewalDate).toLocaleDateString("en-GB") : "-"}</div>

                                                                                                    {vfc.uploadDocument && (
                                                                                                        <div>
                                                                                                            <a
                                                                                                                href={vfc.uploadDocument}
                                                                                                                target="_blank"
                                                                                                                rel="noreferrer"
                                                                                                            >
                                                                                                                View Renewal Document
                                                                                                            </a>
                                                                                                        </div>
                                                                                                    )}
                                                                                                </>
                                                                                            )}
                                                                                    </div>
                                                                                ))}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                {selectedCircle && !selectedDivision &&
                                                    Object.values(item.ranges || item.divisions || {}).map((parent) => (
                                                        <div
                                                            key={parent._id}
                                                            style={{
                                                                marginBottom: 15,
                                                                border: "1px solid #E5E7EB",
                                                                borderRadius: 10,
                                                                overflow: "hidden"
                                                            }}
                                                        >
                                                            {/* Range Header */}

                                                            <div
                                                                onClick={() => toggleRange(parent._id)}
                                                                style={{
                                                                    display: "flex",
                                                                    justifyContent: "space-between",
                                                                    alignItems: "center",
                                                                    padding: "12px 15px",
                                                                    cursor: "pointer",
                                                                    background:
                                                                        parent.pending === 0
                                                                            ? "#ECFDF5"
                                                                            : "#FEF2E2"
                                                                }}
                                                            >
                                                                <div>
                                                                    <div
                                                                        style={{
                                                                            fontWeight: 700,
                                                                            color:
                                                                                parent.pending === 0
                                                                                    ? "#166534"
                                                                                    : "#991B1B"
                                                                        }}
                                                                    >
                                                                        Range : {parent.name}
                                                                    </div>

                                                                    <div
                                                                        style={{
                                                                            fontSize: 12,
                                                                            color: "#666"
                                                                        }}
                                                                    >
                                                                        {parent.completed}/{parent.totalVfcs} Completed
                                                                    </div>
                                                                </div>

                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: 15
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            padding: "4px 12px",
                                                                            borderRadius: 20,
                                                                            background:
                                                                                parent.pending === 0
                                                                                    ? "#DCFCE7"
                                                                                    : "#FEE2E2",
                                                                            color:
                                                                                parent.pending === 0
                                                                                    ? "#166534"
                                                                                    : "#991B1B",
                                                                            fontSize: 12,
                                                                            fontWeight: 600
                                                                        }}
                                                                    >
                                                                        {parent.pending === 0
                                                                            ? "Completed"
                                                                            : "Pending"}
                                                                    </span>

                                                                    <i
                                                                        className={`mdi ${expandedRanges[parent._id]
                                                                            ? "mdi-chevron-up"
                                                                            : "mdi-chevron-down"
                                                                            }`}
                                                                        style={{ fontSize: 22 }}
                                                                    />
                                                                </div>
                                                            </div>

                                                            {/* Expanded Range */}

                                                            {expandedRanges[parent._id] && (
                                                                <div
                                                                    style={{
                                                                        marginLeft: 20,
                                                                        marginTop: 10
                                                                    }}
                                                                >
                                                                    {parent.vfcs.map((vfc) => (
                                                                        <div
                                                                            key={vfc._id}
                                                                            style={{
                                                                                marginBottom: 10,
                                                                                border: "1px solid #E5E7EB",
                                                                                borderRadius: 8,
                                                                                background: "#fff",
                                                                                padding: 12
                                                                            }}
                                                                        >
                                                                            <div
                                                                                style={{
                                                                                    display: "flex",
                                                                                    justifyContent: "space-between",
                                                                                    alignItems: "center"
                                                                                }}
                                                                            >
                                                                                <strong>{vfc.name}</strong>

                                                                                <span
                                                                                    style={{
                                                                                        padding: "4px 10px",
                                                                                        borderRadius: 20,
                                                                                        background:
                                                                                            vfc.completed
                                                                                                ? "#DCFCE7"
                                                                                                : "#FEE2E2",
                                                                                        color:
                                                                                            vfc.completed
                                                                                                ? "#166534"
                                                                                                : "#991B1B",
                                                                                        fontSize: 11,
                                                                                        fontWeight: 600
                                                                                    }}
                                                                                >
                                                                                    {vfc.completed
                                                                                        ? "Completed"
                                                                                        : "Pending"}
                                                                                </span>
                                                                                <div
                                                                                    style={{
                                                                                        fontSize: 12,
                                                                                        color: "#666",
                                                                                        marginTop: 5
                                                                                    }}
                                                                                >
                                                                                    Last Updated :
                                                                                    {formatLastUpdated(vfc.lastUpdatedDate)}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                {selectedDivision &&
                                                    (item.vfcs || []).map((vfc) => (
                                                        <div
                                                            key={vfc._id}
                                                            style={{
                                                                border: "1px solid #E5E7EB",
                                                                borderRadius: 8,
                                                                padding: 12,
                                                                marginBottom: 12,
                                                                background: "#fff"
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    display: "flex",
                                                                    justifyContent: "space-between",
                                                                    alignItems: "center",
                                                                    marginBottom: 10
                                                                }}
                                                            >
                                                                <strong>{vfc.name}</strong>

                                                                <span
                                                                    style={{
                                                                        padding: "4px 10px",
                                                                        borderRadius: 20,
                                                                        background: vfc.completed
                                                                            ? "#DCFCE7"
                                                                            : "#FEE2E2",
                                                                        color: vfc.completed
                                                                            ? "#166534"
                                                                            : "#991B1B",
                                                                        fontSize: 11,
                                                                        fontWeight: 600
                                                                    }}
                                                                >
                                                                    {vfc.completed ? "Completed" : "Pending"}
                                                                </span>
                                                                <div
                                                                    style={{
                                                                        fontSize: 12,
                                                                        color: "#666",
                                                                        marginTop: 5
                                                                    }}
                                                                >
                                                                    Last Updated :
                                                                    {formatLastUpdated(vfc.lastUpdatedDate)}
                                                                </div>
                                                            </div>

                                                            {/* ---------------- Audit ---------------- */}

                                                            {vfc.completed &&
                                                                (selectedType || "audit").toLowerCase() === "audit" && (
                                                                    <div
                                                                        style={{
                                                                            display: "grid",
                                                                            gridTemplateColumns: "repeat(3,1fr)",
                                                                            gap: 12
                                                                        }}
                                                                    >
                                                                        <div>
                                                                            <strong>Audit Date</strong>
                                                                            <br />
                                                                            {vfc.auditDate
                                                                                ? new Date(vfc.auditDate).toLocaleDateString(
                                                                                    "en-GB"
                                                                                )
                                                                                : "-"}
                                                                        </div>

                                                                        <div>
                                                                            <strong>Auditor</strong>
                                                                            <br />
                                                                            {vfc.auditorName || "-"}
                                                                        </div>

                                                                        <div>
                                                                            <strong>Balance</strong>
                                                                            <br />
                                                                            ₹{" "}
                                                                            {Number(
                                                                                vfc.balanceDuringAudit || 0
                                                                            ).toLocaleString("en-IN")}
                                                                        </div>

                                                                        {vfc.uploadDocument && (
                                                                            <div
                                                                                style={{
                                                                                    gridColumn: "1 / span 3"
                                                                                }}
                                                                            >
                                                                                <strong>Document</strong>
                                                                                <br />

                                                                                <a
                                                                                    href={vfc.uploadDocument}
                                                                                    target="_blank"
                                                                                    rel="noreferrer"
                                                                                    style={{
                                                                                        color: "#1976D2",
                                                                                        textDecoration: "none"
                                                                                    }}
                                                                                >
                                                                                    View Audit Document
                                                                                </a>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}

                                                            {/* ---------------- Renewal ---------------- */}

                                                            {vfc.completed &&
                                                                (selectedType || "audit").toLowerCase() === "renewal" && (
                                                                    <div
                                                                        style={{
                                                                            display: "grid",
                                                                            gridTemplateColumns: "repeat(2,1fr)",
                                                                            gap: 12
                                                                        }}
                                                                    >
                                                                        <div>
                                                                            <strong>Renewal No</strong>
                                                                            <br />
                                                                            {vfc.renewalNo || "-"}
                                                                        </div>

                                                                        <div>
                                                                            <strong>Renewal Date</strong>
                                                                            <br />
                                                                            {vfc.renewalDate
                                                                                ? new Date(
                                                                                    vfc.renewalDate
                                                                                ).toLocaleDateString("en-GB")
                                                                                : "-"}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                        </div>
                                                    ))}

                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    </div>;
};


export default AuditRenewalReview;
